import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeadingSection from '../../../../common/pageheading/PageHeadingSection';
import apiServices from '../../../../../services/apiService';
import CustomSelect from '../../../../common/formfields/CustomSelect';
import { primaryColor, secondaryColor } from '../../../../../utils/comon';
import { getCountries, getStatesByCountry, getCitiesByState, fetchLocationByPinCode } from '../../../../../utils/locationService';
import Breadcrumbs from '../../../../common/BreadCrum/BreadCrum';

const SiteForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(false);

    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);

    const [formData, setFormData] = useState({
        countryName: null,
        stateName: null,
        cityName: null,
        pincode: '',
        siteName: '',
        isActive: 'Active'
    });

    const isEdit = !!location.state?.id;
    const editId = location.state?.id;

    // Fetch Countries on load
    useEffect(() => {
        const fetchCountries = async () => {
            const data = await getCountries();
            setCountries(data);
        };
        fetchCountries();
    }, []);

    // Fetch States when Country changes
    useEffect(() => {
        if (!formData.countryName) {
            setStates([]);
            return;
        }
        const fetchStates = async () => {
            const data = await getStatesByCountry(formData.countryName);
            setStates(data);
        };
        fetchStates();
    }, [formData.countryName]);

    // Fetch Cities when State changes
    useEffect(() => {
        if (!formData.stateName) {
            setCities([]);
            return;
        }
        const fetchCities = async () => {
            const data = await getCitiesByState(formData.countryName, formData.stateName);
            setCities(data);
        };
        fetchCities();
    }, [formData.countryName, formData.stateName]);

    // Fetch initial data if editing
    useEffect(() => {
        if (isEdit) {
            const fetchSiteData = async () => {
                setIsFetching(true);
                try {
                    const res = await apiServices({ method: 'GET', url: `/Residential/ResSiteDetailsMaster/GetSiteDetailsById/${editId}` });
                    if (res.success && (res.data?.IsSuccess || res.data?.isSuccess || res.data?.success || res.IsSuccess !== false)) {
                        let data = res.data?.Data?.[0] || res.data?.data?.[0] || res.data?.[0] || {};

                        setFormData({
                            countryName: data.CountryName || data.countryName || null,
                            stateName: data.StateName || data.stateName || null,
                            cityName: data.CityName || data.cityName || null,
                            pincode: data.Pincode || data.pincode || '',
                            siteName: data.SiteName || data.siteName || '',
                            isActive: data.IsActive || data.isActive || 'Active'
                        });
                    }
                } catch (error) {
                    console.error("Error fetching site details:", error);
                    toast.error("Failed to fetch site details");
                } finally {
                    setIsFetching(false);
                }
            };
            fetchSiteData();
        }
    }, [isEdit, editId]);

    const handlePincodeChange = async (e) => {
        const value = e.target.value;
        setFormData(prev => ({ ...prev, pincode: value }));

        if (value.length >= 6) {
            const res = await fetchLocationByPinCode(value);
            if (res.success && res.results && res.results.length > 0) {
                const location = res.results[0];
                setFormData(prev => ({
                    ...prev,
                    countryName: location.country,
                    stateName: location.state,
                    cityName: location.city
                }));
            }
        }
    };

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({ ...prev, [id]: value }));
    };

    const handleSelectChange = (key, option) => {
        setFormData(prev => {
            const newData = { ...prev, [key]: option ? option.value : null };
            if (key === 'countryName') {
                newData.stateName = null;
                newData.cityName = null;
            } else if (key === 'stateName') {
                newData.cityName = null;
            }
            return newData;
        });
    };

    const validateForm = () => {
        if (!formData.countryName) return toast.warning("Country is required"), false;
        if (!formData.stateName) return toast.warning("State is required"), false;
        if (!formData.cityName) return toast.warning("City is required"), false;
        if (!formData.pincode) return toast.warning("Pincode is required"), false;
        if (!formData.siteName || !formData.siteName.trim()) return toast.warning("Site Name is required"), false;
        if (!formData.isActive) return toast.warning("Status is required"), false;
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        setIsLoading(true);

        const payload = {
            siteName: formData.siteName,
            countryName: formData.countryName,
            stateName: formData.stateName,
            cityName: formData.cityName,
            pincode: Number(formData.pincode),
            createdBy: 'sys',
            isActive: formData.isActive === 'Active' ? 1 : 0
        };

        try {
            const url = isEdit ? `/Residential/ResSiteDetailsMaster/UpdateExistingSiteDetails/${editId}` : `/Residential/ResSiteDetailsMaster/AddNewSiteDetails`;
            const method = isEdit ? 'PUT' : 'POST';

            const response = await apiServices({ method, url, data: payload });

            if (response.success && (response.data?.IsSuccess || response.data?.isSuccess || response.data?.success || response.IsSuccess)) {
                toast.success(response.data?.Message || response.data?.message || `Site ${isEdit ? 'updated' : 'created'} successfully!`);
                try {
                    await apiServices({ method: 'GET', url: '/Residential/ResSiteDetailsMaster/getAllSiteDetails' });
                } catch (err) {
                    console.error("Failed to fetch latest site data:", err);
                }
                navigate('/zentres/site-management/site');
            } else {
                toast.error(response.data?.Message || response.data?.message || response.message || `Failed to ${isEdit ? 'update' : 'create'} site.`);
            }
        } catch (error) {
            console.error("Submission error:", error);
            toast.error("An error occurred during submission.");
        } finally {
            setIsLoading(false);
        }
    };

    if (isFetching) {
        return (
            <div className="flex-1 flex justify-center items-center h-full">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-4xl text-gray-300" />
            </div>
        );
    }

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4">
            <Breadcrumbs />
            <PageHeadingSection
                Navbar={true}
                mainHeading={`${isEdit ? 'Update' : 'Add'} Site`}
                subHeading={`Please fill out the form below to ${isEdit ? 'update the existing' : 'create a new'} site.`}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="pincode">
                        Pincode <span className="text-red-500">*</span>
                    </label>
                    <input
                        className="w-full px-3 py-2 border border-gray-200 dark:border-bordercolor rounded-md text-sm outline-none bg-transparent text-textprimary placeholder:text-gray-400 focus:border-gray-400 dark:focus:border-gray-500 transition-colors"
                        type="number"
                        id="pincode"
                        value={formData.pincode}
                        onChange={handlePincodeChange}
                        placeholder="e.g., 400001"
                    />
                </div>

                <CustomSelect
                    label={<>Country <span className="text-red-500">*</span></>}
                    options={countries}
                    value={countries.find(c => c.value === formData.countryName) || null}
                    onChange={(opt) => handleSelectChange('countryName', opt)}
                />

                <CustomSelect
                    label={<>State <span className="text-red-500">*</span></>}
                    options={states}
                    value={states.find(s => s.value === formData.stateName) || null}
                    onChange={(opt) => handleSelectChange('stateName', opt)}
                    disabled={!formData.countryName}
                />

                <CustomSelect
                    label={<>City <span className="text-red-500">*</span></>}
                    options={cities}
                    value={cities.find(c => c.value === formData.cityName) || null}
                    onChange={(opt) => handleSelectChange('cityName', opt)}
                    disabled={!formData.stateName}
                />

                <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="siteName">
                        Site Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        className="w-full px-3 py-2 border border-gray-200 dark:border-bordercolor rounded-md text-sm outline-none bg-transparent text-textprimary placeholder:text-gray-400 focus:border-gray-400 dark:focus:border-gray-500 transition-colors"
                        type="text"
                        id="siteName"
                        value={formData.siteName}
                        onChange={handleInputChange}
                        placeholder="e.g., Phoenix Market City"
                    />
                </div>

                <CustomSelect
                    label={<>Status <span className="text-red-500">*</span></>}
                    options={[{ label: 'Active', value: 'Active' }, { label: 'Inactive', value: 'Inactive' }]}
                    value={formData.isActive ? { label: formData.isActive, value: formData.isActive } : null}
                    onChange={(opt) => handleSelectChange('isActive', opt)}
                />
            </div>

            <div className="flex justify-between mt-auto border-t pt-4">
                <button
                    onClick={() => navigate("/zentres/site-management/site")}
                    className="px-3 py-2 border dark:border-bordercolor rounded-md text-xs flex items-center gap-2 text-gray-600 dark:text-textprimary hover:bg-gray-50 dark:hover:bg-actionitembg transition-colors"
                >
                    <FontAwesomeIcon icon={faAngleLeft} />
                    Cancel
                </button>

                <button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="px-4 py-2 rounded-md text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200"
                    style={{ backgroundColor: secondaryColor(), color: primaryColor() }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = primaryColor();
                        e.currentTarget.style.color = '#FFFFFF';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = secondaryColor();
                        e.currentTarget.style.color = primaryColor();
                    }}
                >
                    {isLoading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : (isEdit ? 'Update' : 'Submit')}
                    <FontAwesomeIcon icon={faAngleRight} />
                </button>
            </div>
        </div>
    );
};

export default SiteForm;
